import {useCallback, useMemo} from 'react'
import {omit} from 'lodash'
import {SortValue} from './ControlledFilterTable'
import {AdvancedFilterTypes, FilterModel} from '../../../models/FilterModel'
import { useSelectedYear } from '../../../modules/common/pages/SelectedYearContext'

export interface UseControlledFilterTableHelpersOptions {
  onFilter?: (filters: FilterModel) => void
  filters: FilterModel
  initialFilters?: FilterModel
}

export const useControlledFilterTableHelpers = ({
  filters,
  onFilter,
  initialFilters,
}: UseControlledFilterTableHelpersOptions) => {
  const { selectedYear } = useSelectedYear()

  const getAdvancedFilterValue = useCallback(
    (key: string) => {
      return filters?.filters?.[key]
    },
    [filters?.filters]
  )

  const setAdvancedFilterValue = useCallback(
    (key: string, value: AdvancedFilterTypes) => {
      const newFilters = {
        ...filters,
        filters: {
          ...filters?.filters,
          [key]: value,
          year: selectedYear,
        },
      }
      onFilter?.(newFilters)
    },
    [filters, onFilter, selectedYear]
  )

  const setAdvancedFilterValues = useCallback(
    (values: Partial<Record<string, AdvancedFilterTypes>>) => {
      const newValue = {
        ...filters,
        filters: {
          ...filters.filters,
          ...values,
        },
      }
      Object.keys(newValue.filters).forEach((key) => {
        if (newValue.filters[key] === undefined) {
          delete newValue.filters[key]
        }
      })
      onFilter?.(newValue)
    },
    [filters, onFilter]
  )

  const clearFilters = useCallback(() => {
    onFilter?.({
      ...initialFilters,
      filters: {
        ...omit(initialFilters?.filters, 'search'),
      },
    })
  }, [initialFilters, onFilter])

  const clearAdvancedFilterValue = useCallback(
    (key: string | string[]) => {
      const newValue = {...filters, filters: {...filters.filters}}

      if (typeof key === 'string') {
        delete newValue.filters[key]
      } else {
        key.forEach((key) => {
          delete newValue.filters[key]
        })
      }
      onFilter?.(newValue)
    },
    [filters, onFilter]
  )

  const hasFilters = useMemo(() => {
    const initialAdvancedFilters = omit(initialFilters?.filters || {}, 'search')
    return (
      Boolean(filters.filters?.search) ||
      hasAdvancedFilterChanged(initialAdvancedFilters, omit(filters?.filters, 'search'))
    )
  }, [filters, initialFilters?.filters])

  const setPageNumber = useCallback(
    (page: number) => {
      onFilter?.({
        ...filters,
        page,
      })
    },
    [filters, onFilter]
  )

  const setPageSize = useCallback(
    (pageSize: number) => {
      onFilter?.({
        ...filters,
        limit: pageSize,
      })
    },
    [filters, onFilter]
  )

  const search = useCallback(
    (search: string) => {
      onFilter?.({
        ...filters,
        filters: {
          ...filters?.filters,
          search,
          year: selectedYear,
        },
      })
    },
    [filters, onFilter, selectedYear]
  )

  const setSortedColumn = useCallback(
    (sort: SortValue) => {
      onFilter?.({
        ...filters,
        sortField: sort[0],
        sortOrder: sort[1] ? 'ASC' : 'DESC',
      })
    },
    [filters, onFilter]
  )

  const sortedColumn = useMemo((): SortValue | undefined => {
    if (filters.sortField && filters.sortOrder) {
      return [filters.sortField, filters.sortOrder === 'ASC']
    }
  }, [filters])

  return {
    getAdvancedFilterValue,
    setAdvancedFilterValue,
    setAdvancedFilterValues,
    clearFilters,
    clearAdvancedFilterValue,
    hasFilters,
    setPageNumber,
    setPageSize,
    search,
    setSortedColumn,
    sortedColumn,
  }
}

const hasAdvancedFilterChanged = (
  previous: Required<FilterModel>['filters'],
  current: Required<FilterModel>['filters']
): boolean => {
  return compareEquality(previous, current) || compareEquality(current, previous)
}

// Returns true if b has all fields of a
const compareEquality = (
  a: Required<FilterModel>['filters'],
  b: Required<FilterModel>['filters']
) => {
  return Object.entries(a).some(([key, aValue]) => {
    if (key in b) {
      const bValue = b[key]
      if (typeof bValue === 'string' || typeof bValue === 'boolean') {
        return bValue !== aValue
      } else if (Array.isArray(bValue) && Array.isArray(aValue)) {
        return bValue.length !== aValue.length || bValue.some((value) => !aValue.includes(value))
      }
    }
    return true
  })
}
